import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Package, Plus, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ITEM_TYPES = ['Data Logger', 'Temperature Control Box', 'UN Approved Box', 'Mapping Report'];

/**
 * Multi-add inventory item selector for Docket / Trip forms.
 *
 * Props:
 *   value             { array of selected inventory_item objects (with id, item_type, sr_no, ...) }
 *   onChange          (next-array) => void
 *   excludeDocketId   exclude this docket from the in-use check (for edit mode)
 *   excludeTripId     exclude this trip from the in-use check (for edit mode)
 *   title             section heading (default: "Inventory Items")
 */
export default function InventorySelector({
  value = [], onChange, excludeDocketId, excludeTripId, title = 'Inventory Items',
}) {
  const [available, setAvailable] = useState([]);
  const [draftType, setDraftType] = useState('');
  const [draftItemId, setDraftItemId] = useState('');
  const [loading, setLoading] = useState(false);

  const fetchAvailable = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (excludeDocketId) params.append('exclude_docket_id', excludeDocketId);
      if (excludeTripId) params.append('exclude_trip_id', excludeTripId);
      const res = await fetch(`${API}/api/inventory/available?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
      });
      if (res.ok) setAvailable(await res.json());
    } finally {
      setLoading(false);
    }
  }, [excludeDocketId, excludeTripId]);

  useEffect(() => { fetchAvailable(); }, [fetchAvailable]);

  // Union of available items + items already selected (so edit-mode shows them)
  const allItems = useMemo(() => {
    const map = new Map();
    available.forEach(i => map.set(i.id, i));
    value.forEach(i => { if (!map.has(i.id)) map.set(i.id, i); });
    return Array.from(map.values());
  }, [available, value]);

  const selectedIds = useMemo(() => new Set(value.map(v => v.id)), [value]);

  const filteredByType = allItems.filter(i => !draftType || draftType === '__all__' || i.item_type === draftType);
  // Only show items not already in the selected list
  const pickable = filteredByType.filter(i => !selectedIds.has(i.id));

  const handleAdd = () => {
    if (!draftItemId) return;
    const item = allItems.find(i => i.id === draftItemId);
    if (!item) return;
    onChange([...value, item]);
    setDraftItemId('');
    // keep draftType so the user can quickly add another of same type
  };

  const handleRemove = (id) => onChange(value.filter(v => v.id !== id));

  return (
    <Card className="bg-white border border-gray-100 shadow-sm" data-testid="inventory-selector">
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Package className="w-5 h-5 text-emerald-600" />
          <div>
            <h3 className="text-base font-semibold text-text-dark font-manrope">{title}</h3>
            <p className="text-xs text-text-secondary font-ibm">Pick equipment used for this shipment. An item is locked once linked to a non-final docket or in-progress trip.</p>
          </div>
        </div>
        <span className="text-xs text-text-secondary font-ibm">{value.length} selected</span>
      </div>

      <div className="p-6 space-y-4">
        {/* Picker row */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4">
            <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">Item Type</label>
            <Select value={draftType} onValueChange={(v) => { setDraftType(v); setDraftItemId(''); }}>
              <SelectTrigger data-testid="inv-type-select" className="font-ibm mt-1">
                <SelectValue placeholder="All types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All types</SelectItem>
                {ITEM_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-6">
            <label className="text-xs font-medium text-text-secondary uppercase tracking-wide">Item (by SR No.)</label>
            <Select value={draftItemId} onValueChange={setDraftItemId} disabled={loading || pickable.length === 0}>
              <SelectTrigger data-testid="inv-item-select" className="font-ibm mt-1">
                <SelectValue placeholder={loading ? 'Loading...' : pickable.length === 0 ? 'No items available' : 'Select item...'} />
              </SelectTrigger>
              <SelectContent>
                {pickable.map(i => (
                  <SelectItem key={i.id} value={i.id}>
                    <span className="font-mono text-xs mr-2">{i.sr_no}</span>
                    <span className="text-text-secondary text-xs">— {i.item_type}{i.location ? ` (${i.location})` : ''}</span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-2">
            <Button
              type="button"
              onClick={handleAdd}
              disabled={!draftItemId}
              className="w-full bg-emerald-600 hover:bg-emerald-700 font-ibm"
              data-testid="inv-add-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
          </div>
        </div>

        {/* Selected list */}
        {value.length === 0 ? (
          <div className="text-center py-6 border border-dashed border-gray-200 rounded-lg">
            <Package className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-text-secondary font-ibm">No inventory items linked yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="inv-selected-table">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">#</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Type</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">SR No.</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Manufacturer</th>
                  <th className="px-3 py-2 text-left text-xs font-semibold text-text-secondary uppercase tracking-wider font-ibm">Location</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {value.map((it, idx) => (
                  <tr key={it.id} className="hover:bg-gray-50" data-testid={`inv-row-${it.id}`}>
                    <td className="px-3 py-2 text-sm text-text-secondary font-ibm">{idx + 1}</td>
                    <td className="px-3 py-2 text-sm text-text-dark font-ibm">
                      <div className="flex items-center gap-2">
                        <span>{it.item_type}</span>
                        {(it.auto_link_source === 'data_logger' || it.is_auto_linked) && (
                          <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-1.5 py-0.5 rounded">
                            Auto-linked
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-sm font-mono text-accent-blue">{it.sr_no}</td>
                    <td className="px-3 py-2 text-sm text-text-dark font-ibm">{it.manufacture || '—'}</td>
                    <td className="px-3 py-2 text-sm text-text-secondary font-ibm">{it.location || '—'}</td>
                    <td className="px-3 py-2">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemove(it.id)}
                        data-testid={`inv-remove-${it.id}`}
                      >
                        <Trash2 className="w-4 h-4 text-accent-red" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Card>
  );
}
